import {
  Box3,
  CircleGeometry,
  Color,
  DirectionalLight,
  HemisphereLight,
  Mesh,
  MeshBasicMaterial,
  NoToneMapping,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  Vector3,
  WebGLRenderer,
} from 'three';
import { type EraId, ERAS } from '../config/eras';
import type { PosterPose } from '../ui/posterStyle';
import { buildCar, type CarModel } from './car/carModel';
import { PAL } from './palette';
import { LIGHT } from './world';

/**
 * The day's car as the title poster's illustration: big, head-on three
 * quarters, flat-lit like a print, with a flat shadow under it. Drawn on its
 * own small canvas (transparent, so the poster's colour shows through) once
 * per circuit and on resize.
 */
export class PosterArt {
  readonly canvas: HTMLCanvasElement;
  private renderer: WebGLRenderer | null = null;
  private scene = new Scene();
  private camera = new PerspectiveCamera(22, 1, 0.1, 100);
  private car: CarModel | null = null;
  private era: EraId | null = null;
  private gold = false;
  private pose: PosterPose = { name: 'threeQuarter', azimuth: 40, elevation: 15, fov: 22, fill: 0.62, steer: 0.18, lean: 0.03 };
  private side = 1;
  private box = new Box3();

  /** `keep`: the picture stays readable after drawing (to copy it into another canvas). */
  constructor(private keep = false) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'art-canvas';
    this.scene.add(new HemisphereLight(new Color(LIGHT.skyColor), new Color(LIGHT.groundColor), 0.62 * Math.PI));
    const sun = new DirectionalLight(new Color(LIGHT.sunColor), 0.78 * Math.PI);
    sun.position.set(4, 7, 6);
    this.scene.add(sun);
    const shadow = new Mesh(new CircleGeometry(1, 40), new MeshBasicMaterial({ color: PAL.brown, transparent: true, opacity: 0.28 }));
    shadow.rotation.x = -Math.PI / 2;
    shadow.scale.set(2.5, 1.25, 1);
    shadow.position.set(-0.15, 0.005, 0);
    this.scene.add(shadow);
  }

  /** The day's angle on the car: pose, and which way it faces (+1 / -1). */
  setPose(pose: PosterPose, side: number): void {
    this.pose = pose;
    this.side = side;
    this.applyPose();
  }

  private applyPose(): void {
    if (!this.car) return;
    const { steer, lean } = this.pose;
    for (const w of this.car.wheels) if (w.front) w.steer.rotation.y = steer * this.side;
    this.car.chassis.rotation.x = lean * this.side;
    this.car.root.updateMatrixWorld(true);
    this.box.setFromObject(this.car.root);
  }

  /** The day's car (`gold`: the champion's, made of gold). */
  show(era: EraId, gold = false): void {
    if (era === this.era && gold === this.gold && this.car) return;
    if (this.car) {
      this.scene.remove(this.car.root);
      this.car.root.traverse((o) => (o as Mesh).geometry?.dispose());
      for (const m of this.car.materials) m.dispose();
    }
    this.era = era;
    this.gold = gold;
    this.car = buildCar(ERAS[era].look, false, gold);
    // Wheels straight, scarf streaming as if at speed.
    for (let i = 0; i < 40; i++) this.car.scarf?.update(1 / 60, 24);
    this.scene.add(this.car.root);
    this.applyPose();
  }

  /** Draw at the canvas's current CSS size. */
  render(): void {
    this.drawAt(this.canvas.clientWidth, this.canvas.clientHeight, Math.min(window.devicePixelRatio || 1, 2));
  }

  /** Draw at an exact size (off screen too, for a picture). False if there's nothing to draw. */
  drawAt(w: number, h: number, pixelRatio = 1): boolean {
    if (!this.car || w < 8 || h < 8) return false;
    if (!this.renderer) {
      this.renderer = new WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true, premultipliedAlpha: true, preserveDrawingBuffer: this.keep });
      this.renderer.outputColorSpace = SRGBColorSpace;
      this.renderer.toneMapping = NoToneMapping;
      this.renderer.setClearColor(0x000000, 0);
    }
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(w, h, false);
    const cam = this.camera;
    const pose = this.pose;
    cam.aspect = w / h;
    cam.fov = pose.fov;
    // The car's nose is +x and its left is -z; the pose's azimuth swings round from the nose.
    const az = (pose.azimuth * this.side * Math.PI) / 180;
    const el = (pose.elevation * Math.PI) / 180;
    const dir = new Vector3(Math.cos(el) * Math.cos(az), Math.sin(el), -Math.cos(el) * Math.sin(az));
    const centre = this.box.getCenter(new Vector3());
    const half = this.box.getSize(new Vector3()).multiplyScalar(0.5);
    // Frame the car's box as seen from there: its extent across and up the view decides the distance.
    const f = dir.clone().negate();
    const right = new Vector3().crossVectors(f, new Vector3(0, 1, 0)).normalize();
    const up = new Vector3().crossVectors(right, f);
    const extent = (a: Vector3): number => half.x * Math.abs(a.x) + half.y * Math.abs(a.y) + half.z * Math.abs(a.z);
    const tanV = Math.tan((cam.fov * Math.PI) / 360);
    const tanH = tanV * cam.aspect;
    // In a wide, short frame (a phone's sheet) the car's height is what limits it: let it fill more of the frame.
    const fill = pose.fill + (0.9 - pose.fill) * Math.min(1, Math.max(0, (cam.aspect - 1.6) / 1.2));
    const d = Math.max(extent(right) / (fill * tanH), extent(up) / (fill * tanV)) + extent(f);
    cam.position.copy(centre).addScaledVector(dir, d);
    // Aim a touch below centre so the car sits on the sheet rather than floating.
    cam.lookAt(centre.x, centre.y - half.y * 0.15, centre.z);
    cam.updateProjectionMatrix();
    this.renderer.render(this.scene, cam);
    return true;
  }
}
